/**
 * Sizing for the material log's ID column.
 *
 * The column has to hold two things: the sequence id, which grows over a
 * project's life (165 today, 10000 later), and — on rows carrying AI insights —
 * a hover-revealed "View Specs" button of fixed width beside it. A fixed column
 * width can only be wrong in one direction or the other, and when the id loses
 * the fight it gets squeezed to nothing, which is what made short ids report as
 * truncated and strand their tooltips open. So measure the ids actually loaded.
 */

/** Width for a cell showing nothing but a short id. Also the floor. */
export const MATERIAL_ID_COL_WIDTH = 80;
/** Ceiling, so one pathological id cannot swallow the viewport. */
export const MATERIAL_ID_COL_MAX_WIDTH = 240;
/**
 * The "View Specs" button (antd `size="small"` at `text-[11px]`) plus its
 * `ml-0.5` gutter. Fixed by its own label — only the id beside it varies.
 * Measured against the rendered button: 78px clipped its right edge, so this
 * carries 5px of slack. Re-measure if the label or button size changes.
 */
export const VIEW_SPECS_AFFORDANCE_WIDTH = 90;
/** `--ag-cell-horizontal-padding` (12px) on both sides — ag-grid-override.scss. */
const CELL_HORIZONTAL_PADDING = 24;
/** Used until a real id link is on screen to read the grid's own font from. */
const FALLBACK_ID_FONT = "500 14px sans-serif";
/** Per-character advance used only when canvas 2d is unavailable. */
const FALLBACK_CHAR_WIDTH = 9;

type MaterialIdRow = {
  sequence_id?: string | number | null;
  ai_insights?: Record<string, unknown> | null;
};

let measureCanvas: HTMLCanvasElement | null = null;

function getMeasureContext(): CanvasRenderingContext2D | null {
  if (typeof document === "undefined") return null;
  if (!measureCanvas) measureCanvas = document.createElement("canvas");
  const ctx = measureCanvas.getContext?.("2d") ?? null;
  if (!ctx) return null;

  // Prefer the font the grid is actually rendering ids in; a rendered cell is
  // available by the time this runs (it is gated on first data render).
  const sample =
    typeof document.querySelector === "function"
      ? document.querySelector(".gridIdLink")
      : null;
  const style =
    sample && typeof window?.getComputedStyle === "function"
      ? window.getComputedStyle(sample)
      : null;
  ctx.font = style?.fontSize
    ? `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`
    : FALLBACK_ID_FONT;
  return ctx;
}

function widestTextWidth(
  texts: string[],
  ctx: CanvasRenderingContext2D | null
): number {
  if (!texts.length) return 0;
  // Ids are numeric, so only the longest strings can be the widest — measuring
  // the rest is wasted work on a log with thousands of rows.
  const longest = texts.reduce((len, text) => Math.max(len, text.length), 0);
  if (!ctx?.measureText) return longest * FALLBACK_CHAR_WIDTH;
  return texts.reduce(
    (widest, text) =>
      text.length === longest
        ? Math.max(widest, ctx.measureText(text).width)
        : widest,
    0
  );
}

/**
 * Width that fits the widest id in `rows`, plus the "View Specs" button on the
 * rows that show one. Clamped to [MATERIAL_ID_COL_WIDTH, MATERIAL_ID_COL_MAX_WIDTH].
 */
export function getMaterialIdColumnWidth(
  rows: ReadonlyArray<MaterialIdRow> | null | undefined
): number {
  const plainIds: string[] = [];
  const idsBesideButton: string[] = [];

  (rows ?? []).forEach((row) => {
    const id = row?.sequence_id;
    if (id === null || id === undefined || id === "") return;
    if (Object.keys(row?.ai_insights ?? {}).length > 0) {
      idsBesideButton.push(String(id));
    } else {
      plainIds.push(String(id));
    }
  });

  const ctx = getMeasureContext();
  const widestContent = Math.max(
    widestTextWidth(plainIds, ctx),
    idsBesideButton.length
      ? widestTextWidth(idsBesideButton, ctx) + VIEW_SPECS_AFFORDANCE_WIDTH
      : 0
  );

  return Math.min(
    MATERIAL_ID_COL_MAX_WIDTH,
    Math.max(
      MATERIAL_ID_COL_WIDTH,
      Math.ceil(widestContent + CELL_HORIZONTAL_PADDING)
    )
  );
}
